import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import SectionTitle from "@/components/base/SectionTitle";
import WorkCard from "@/components/base/WorkCard";
import NewsItem from "@/components/base/NewsItem";
import HeroCarousel from "./components/HeroCarousel";
import { heroSlides, quickCategories, siteStats } from "@/mocks/home";
import { worksList } from "@/mocks/works";
import { newsList } from "@/mocks/news";

export default function Home() {
  const hotWorks = [...worksList].sort((a, b) => b.rating - a.rating).slice(0, 8);
  const topNews = newsList.slice(0, 5);
  const latestNews = newsList.slice(0, 4);

  const worksByType = (type: string) =>
    worksList.filter((w) => w.type === type).slice(0, 4);

  return (
    <Layout>
      {/* Hero 区 */}
      <section className="relative bg-primary-950 -mt-16 md:-mt-20">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20cinema%20theater%20interior%20with%20deep%20navy%20seats%20and%20warm%20amber%20screen%20glow%2C%20blurred%20bokeh%20lights%2C%20premium%20moody%20atmosphere%2C%20cinematic%20wide%20shot%2C%20dark%20elegant%20background&width=1920&height=1080&seq=aa-home-hero-bg&orientation=landscape"
            alt="爱爱网影视门户"
            title="爱爱网 - 最新影视资讯与热门推荐"
            className="w-full h-full object-cover object-top opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary-950/70 via-primary-950/60 to-primary-950/95" />
        </div>

        <div className="relative w-full px-4 md:px-8 pt-24 md:pt-32 pb-10 md:pb-14">
          <div className="text-center max-w-3xl mx-auto mb-8 md:mb-10 animate-fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/15 text-white/90 text-xs md:text-sm mb-5">
              <i className="ri-fire-fill text-accent-400" />
              每日更新影视资讯 · 追剧观影不迷路
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-white leading-tight">
              发现值得一看的好影视
            </h1>
            <p className="mt-4 text-sm md:text-base text-white/75 leading-relaxed">
              爱爱网汇集最新影视资讯、热门电视剧与精选电影，为你提供清晰的观影决策与丰富的内容体验。
            </p>
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/电视剧"
                className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-tv-2-line" />
                浏览热门剧集
              </Link>
              <Link
                to="/news"
                className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-newspaper-line" />
                查看最新资讯
              </Link>
            </div>
          </div>

          {/* 轮播 + 资讯推荐 */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 animate-scale-in">
              <HeroCarousel slides={heroSlides} />
            </div>
            <div className="rounded-xl bg-background-50 border border-background-200 p-5 animate-fade-up">
              <div className="flex items-center gap-2 mb-3 pb-3 border-b border-background-200">
                <span className="w-8 h-8 rounded-md bg-primary-100 text-primary-700 flex items-center justify-center">
                  <i className="ri-flashlight-line" />
                </span>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  资讯推荐
                </h2>
              </div>
              <div className="flex flex-col">
                {topNews.map((news, i) => (
                  <Link
                    key={news.id}
                    to={`/news/${news.id}`}
                    className="flex items-start gap-3 py-2.5 border-b border-background-100 last:border-0 group cursor-pointer"
                  >
                    <span
                      className={`shrink-0 w-5 h-5 rounded text-[11px] font-bold flex items-center justify-center ${
                        i < 3
                          ? "bg-accent-500 text-primary-950"
                          : "bg-background-200 text-foreground-500"
                      }`}
                    >
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground-800 line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {news.title}
                      </p>
                      <span className="text-xs text-foreground-400">{news.date}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 分类快捷入口 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <SectionTitle
          icon="ri-apps-2-line"
          title="影视推荐快捷入口"
          subtitle="按类型快速进入，发现你的下一部心头好"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {quickCategories.map((cat) => (
            <Link
              key={cat.type}
              to={`/category/${cat.type}`}
              className="group relative rounded-xl overflow-hidden border border-background-200 bg-background-50 p-6 hover:border-primary-300 transition-all duration-300 cursor-pointer animate-fade-up"
            >
              <div className="flex items-center justify-between mb-4">
                <span className="w-12 h-12 rounded-lg bg-primary-500 text-background-50 flex items-center justify-center">
                  <i className={`${cat.icon} text-2xl`} />
                </span>
                <i className="ri-arrow-right-up-line text-2xl text-foreground-300 group-hover:text-accent-500 group-hover:translate-x-1 group-hover:-translate-y-1 transition-all" />
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950">
                {cat.label}
              </h3>
              <p className="mt-1 text-sm text-foreground-500">{cat.desc}</p>
              <div className="mt-5 flex gap-2 overflow-hidden">
                {worksByType(cat.type)
                  .slice(0, 4)
                  .map((w) => (
                    <div
                      key={w.id}
                      className="w-14 h-20 shrink-0 rounded-md overflow-hidden bg-background-200"
                    >
                      <img
                        src={w.cover}
                        alt={w.title}
                        title={`${w.title} - 爱爱网影视推荐`}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ))}
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 热门影视作品 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-background-100 border-y border-background-200">
        <SectionTitle
          icon="ri-award-line"
          title="热门影视作品"
          subtitle="本季高口碑与高热度作品精选"
          moreTo="/category/电影"
          moreLabel="更多作品"
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
          {hotWorks.map((work) => (
            <WorkCard key={work.id} work={work} />
          ))}
        </div>
      </section>

      {/* 最新影视资讯 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16">
        <SectionTitle
          icon="ri-newspaper-line"
          title="最新影视资讯"
          subtitle="第一时间掌握影视动态与行业观察"
          moreTo="/news"
          moreLabel="全部资讯"
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10">
          {newsList.slice(0, 6).map((news) => (
            <NewsItem key={news.id} news={news} />
          ))}
        </div>
      </section>

      {/* 网站简介 */}
      <section className="w-full px-4 md:px-8 py-12 md:py-16 bg-primary-950 text-white relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center relative">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-xs mb-4">
              <i className="ri-information-line text-accent-400" />
              关于爱爱网
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold leading-snug">
              一个为影视爱好者打造的资讯与推荐门户
            </h2>
            <p className="mt-4 text-sm md:text-base text-primary-200/85 leading-relaxed">
              我们围绕影视资讯与作品展示两大核心，持续整理最新动态、口碑作品与观影指南，
              帮助你在海量内容中快速找到值得投入时间的那一部。
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                to="/news"
                className="inline-flex items-center justify-center gap-2 px-5 h-11 rounded-md bg-accent-500 hover:bg-accent-600 text-primary-950 text-sm font-bold transition-colors cursor-pointer whitespace-nowrap"
              >
                开始阅读资讯
              </Link>
              <Link
                to="/category/电影"
                className="inline-flex items-center justify-center gap-2 px-5 h-11 rounded-md border border-white/25 hover:bg-white/10 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                探索电影分类
              </Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {siteStats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl bg-white/5 border border-white/10 p-5"
              >
                <p className="font-heading text-2xl md:text-3xl font-bold text-accent-400">
                  {stat.value}
                </p>
                <p className="mt-1 text-xs md:text-sm text-primary-200/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}